import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listBiosites } from '../api';
import { Button } from '../components/ui/button';
import { ArrowRight, QrCode, Link2, Palette, Lock, Sparkles, LogIn, Loader2 } from 'lucide-react';

export default function Landing() {
  const [biosites, setBiosites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await listBiosites();
        setBiosites(data || []);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const demoSlug = biosites[0]?.slug || 'joaosilva';

  return (
    <div className="min-h-screen bg-[#faf7f2] text-neutral-900">
      <nav className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display font-bold text-xl">
          <span className="w-8 h-8 rounded-lg bg-neutral-900 text-white grid place-items-center">
            <Link2 size={16} />
          </span>
          Biolink•br
        </Link>
        <Link to="/admin/login">
          <Button variant="outline" className="gap-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white">
            <LogIn size={16} /> Login Admin
          </Button>
        </Link>
      </nav>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-16 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-3 py-1 text-xs font-medium border border-neutral-200">
            <Sparkles size={12} className="text-amber-500" /> Biosites com PIX integrado
          </div>
          <h1 className="mt-5 text-5xl md:text-6xl font-display font-bold leading-[1.05] tracking-tight">
            Um link,<br />
            <span className="italic font-serif">todas as suas</span><br />
            conexões.
          </h1>
          <p className="mt-5 text-neutral-600 text-lg max-w-md">
            Crie biosites lindos para seus clientes, receba por PIX e edite tudo em segundos. Simples assim.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/admin/login">
              <Button className="bg-neutral-900 hover:bg-neutral-800 text-white gap-2 h-11 px-6">
                Entrar como admin <ArrowRight size={16} />
              </Button>
            </Link>
            <Link to={`/${demoSlug}`}>
              <Button variant="outline" className="border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white h-11 px-6">
                Ver demo
              </Button>
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[
              { icon: QrCode, label: 'PIX QR Code' },
              { icon: Palette, label: 'Temas prontos' },
              { icon: Lock, label: 'Painel seguro' }
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="bg-white rounded-xl p-3 border border-neutral-200">
                <Icon size={18} className="text-neutral-900" />
                <div className="text-xs mt-2 font-medium">{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex justify-center">
          <div className="w-[280px] h-[560px] rounded-[2.5rem] border-[10px] border-neutral-900 overflow-hidden shadow-2xl bg-gradient-to-b from-orange-400 via-rose-500 to-pink-700">
            <div className="h-full w-full p-5 flex flex-col items-center">
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=faces" className="w-20 h-20 rounded-full object-cover border-2 border-white mt-6" alt="" />
              <div className="text-white font-bold mt-3">@joaosilva</div>
              <div className="text-white/90 text-xs mt-1 text-center">Fotógrafo profissional</div>
              <div className="w-full space-y-2.5 mt-6">
                {['Portfólio Completo', 'Agende sua sessão', 'Me pague um café • PIX'].map((t, i) => (
                  <div key={i} className="bg-white rounded-xl py-3 text-center text-xs font-semibold text-neutral-900">{t}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-neutral-200">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-3xl font-display font-bold tracking-tight">Biosites públicos</h2>
            <p className="text-neutral-600 mt-1">Clique para visitar qualquer página.</p>
          </div>
        </div>
        {loading ? (
          <div className="grid place-items-center py-10"><Loader2 className="animate-spin text-neutral-400" /></div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5">
            {biosites.map((b) => (
              <Link to={`/${b.slug}`} key={b.slug}
                className="group bg-white rounded-2xl p-5 border border-neutral-200 hover:border-neutral-900 transition">
                <div className="flex items-center gap-3">
                  <img src={b.avatar} className="w-14 h-14 rounded-full object-cover" alt="" />
                  <div>
                    <div className="font-bold font-display">{b.name}</div>
                    <div className="text-xs text-neutral-500">/{b.slug}</div>
                  </div>
                </div>
                <p className="text-sm text-neutral-600 mt-3 line-clamp-2">{b.bio}</p>
                <div className="flex gap-2 mt-4 text-xs">
                  <span className="bg-neutral-100 rounded-full px-2.5 py-1">{b.links.length} links</span>
                  <span className="bg-neutral-100 rounded-full px-2.5 py-1">{b.pix?.length || 0} PIX</span>
                </div>
                <div className="mt-4 text-sm font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  Visitar <ArrowRight size={14} />
                </div>
              </Link>
            ))}
            {biosites.length === 0 && (
              <div className="col-span-full text-center text-neutral-500 py-10">Nenhum biosite público ainda.</div>
            )}
          </div>
        )}
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-sm text-neutral-500 border-t border-neutral-200">
        © 2025 Biolink•br • feito com cuidado
      </footer>
    </div>
  );
}
